import { useEffect } from 'react';

import { GoalDto } from '@/api/generated';
import { ContentRow, GoalsContainer } from '@/pages/GoalsPage/GoalsStyles';
import GeneratingStrategicPlan from '@/pages/GoalsPage/components/GeneratingStrategicPlan';
import GoalBox from '@/pages/GoalsPage/components/GoalBox';
import PeriodDropdown from '@/pages/GoalsPage/components/PeriodDropdown';
import useStrategicPlan from '@/pages/GoalsPage/hooks/useStrategicPlan';
import PageContainer from '@/pages/PageContainer';
import { useOnboardingActions } from '@/store/onboardingStore';
import { QuarterYear } from '@/utils/dateUtil';

import StrategicPlan from './StrategicPlan';

interface GoalsProps {
  periods: QuarterYear[];
  selectedPeriod: QuarterYear;
  setSelectedPeriod: (period: QuarterYear) => void;
  sortedGoals: GoalDto[];
  dueDate: string;
  generatePlan: () => void;
  isGenerating: boolean;
  generatedStrategicPlan: string | null;
  isGenerateError: boolean;
  monthToStart: number;
}

const Goals = ({
  periods,
  selectedPeriod,
  setSelectedPeriod,
  sortedGoals,
  dueDate,
  generatePlan,
  isGenerating,
  generatedStrategicPlan,
  isGenerateError,
  monthToStart
}: GoalsProps) => {
  const { setIsGenerateButtonVisible } = useOnboardingActions();
  const { strategicPlan, onEditStrategicPlan, createStrategicPlan, isLoadingStrategicPlans } =
    useStrategicPlan(selectedPeriod.year, selectedPeriod.quarter);

  useEffect(() => {
    if (!isLoadingStrategicPlans && !strategicPlan) {
      createStrategicPlan(selectedPeriod.year, selectedPeriod.quarter, () => {
        setIsGenerateButtonVisible(true);
      });
    }
  }, [
    isLoadingStrategicPlans,
    strategicPlan,
    createStrategicPlan,
    selectedPeriod.year,
    selectedPeriod.quarter
  ]);

  useEffect(() => {
    if (generatedStrategicPlan) {
      setIsGenerateButtonVisible(false);
    }
  }, [generatedStrategicPlan]);

  return (
    <PageContainer
      isBackButtonVisible={false}
      sx={{ paddingBottom: '40px', backgroundColor: 'transparent' }}
    >
      <ContentRow>
        {!isGenerating && !generatedStrategicPlan && !isGenerateError && (
          <GoalsContainer>
            <PeriodDropdown
              periods={periods}
              selectedPeriod={selectedPeriod}
              onPeriodChange={setSelectedPeriod}
            />
            {Array.from({ length: 3 }).map((_, index) => (
              <GoalBox
                key={index}
                dueDate={dueDate || ''}
                goal={sortedGoals?.[index] ? sortedGoals[index] : undefined}
              />
            ))}
          </GoalsContainer>
        )}
        {isGenerating && <GeneratingStrategicPlan />}
        {(!!generatedStrategicPlan || isGenerateError) && (
          <StrategicPlan
            strategicPlan={strategicPlan}
            onEditStrategicPlan={onEditStrategicPlan}
            selectedPeriod={selectedPeriod}
            goals={sortedGoals}
            monthToStart={monthToStart}
            generatePlan={generatePlan}
            isGenerating={isGenerating}
            generatedStrategicPlan={generatedStrategicPlan}
            isGenerateError={isGenerateError}
          />
        )}
      </ContentRow>
    </PageContainer>
  );
};

export default Goals;
